import React from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { SERVICES_DATA, SERVICE_SLUGS } from "../lib/servicesData";
import { COMPANY } from "../lib/seo";
import { gaEvent, CONVERSION_EVENTS } from "../lib/analytics";

export const Footer = () => {
    return (
        <footer
            data-testid="site-footer"
            className="bg-brand-navy text-brand-stone mt-24 pb-20 sm:pb-0"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <div className="flex items-center gap-4">
                        <img
                            src="/logo.png"
                            alt="Optimum Hospitality Solutions Ltd"
                            className="h-16 w-16 object-contain bg-brand-stone p-1.5 rounded"
                            width="64"
                            height="64"
                            loading="lazy"
                        />
                        <div className="leading-tight">
                            <div className="font-display text-2xl tracking-tight">
                                Optimum
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne mt-1">
                                Hospitality Solutions Ltd
                            </div>
                        </div>
                    </div>
                    <p className="mt-5 text-brand-stone/75 max-w-md leading-relaxed text-sm">
                        Optimum Hospitality Solutions Ltd provides operational consultancy and performance improvement support to hotels, hospitality businesses and multi-site operators throughout the UK.
                    </p>
                    <a
                        href="https://www.linkedin.com/in/sidney-a-a7ab35b7"
                        data-testid="footer-linkedin"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-5 inline-flex items-center gap-2 text-xs text-brand-champagne hover:text-brand-stone"
                    >
                        <Linkedin size={14} />
                        Sidney Adams on LinkedIn
                    </a>
                </div>

                <div className="md:col-span-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne mb-4">
                        Services
                    </div>
                    <ul className="space-y-2.5 text-sm">
                        {SERVICE_SLUGS.map((slug) => (
                            <li key={slug}>
                                <Link
                                    to={`/services/${slug}`}
                                    data-testid={`footer-service-${slug}`}
                                    className="text-brand-stone/85 hover:text-brand-champagne leading-snug block"
                                >
                                    {SERVICES_DATA[slug].title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne mb-4">
                        Navigate
                    </div>
                    <ul className="space-y-2.5 text-sm">
                        <li><Link to="/" data-testid="footer-home" className="text-brand-stone/85 hover:text-brand-champagne">Home</Link></li>
                        <li><Link to="/about" data-testid="footer-about" className="text-brand-stone/85 hover:text-brand-champagne">About</Link></li>
                        <li><Link to="/services" data-testid="footer-services" className="text-brand-stone/85 hover:text-brand-champagne">Services</Link></li>
                        <li><Link to="/industries" data-testid="footer-industries" className="text-brand-stone/85 hover:text-brand-champagne">Industries</Link></li>
                        <li><Link to="/insights" data-testid="footer-insights" className="text-brand-stone/85 hover:text-brand-champagne">Insights</Link></li>
                        <li><Link to="/case-studies" data-testid="footer-case-studies" className="text-brand-stone/85 hover:text-brand-champagne">Case Studies</Link></li>
                        <li><Link to="/booking" data-testid="footer-booking" className="text-brand-stone/85 hover:text-brand-champagne">Book Consultation</Link></li>
                        <li><Link to="/contact" data-testid="footer-contact" className="text-brand-stone/85 hover:text-brand-champagne">Contact</Link></li>
                        <li><Link to="/hospitality-audit-guide" data-testid="footer-guide" className="text-brand-stone/85 hover:text-brand-champagne">Audit Guide</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne mb-4">
                        Contact
                    </div>
                    <ul className="space-y-2 text-sm text-brand-stone/85">
                        <li>
                            <address className="not-italic leading-relaxed">
                                Suite RA01, 195–197 Wood Street<br/>
                                London E17 3NU<br/>
                                United Kingdom
                            </address>
                        </li>
                        <li>
                            <a
                                href={`mailto:${COMPANY.email}`}
                                onClick={() => gaEvent(CONVERSION_EVENTS.EMAIL_CLICK, { location: "footer" })}
                                className="hover:text-brand-champagne break-words"
                                data-testid="footer-email"
                            >
                                {COMPANY.email}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`tel:${COMPANY.telephone.replace(/\s+/g, "")}`}
                                onClick={() => gaEvent(CONVERSION_EVENTS.PHONE_CLICK, { location: "footer" })}
                                className="hover:text-brand-champagne"
                                data-testid="footer-phone"
                            >
                                {COMPANY.telephone}
                            </a>
                        </li>
                    </ul>

                    <div className="mt-6 pt-4 border-t border-white/10 text-xs uppercase tracking-[0.2em] text-brand-champagne mb-3">
                        Legal
                    </div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/privacy-policy" data-testid="footer-privacy" className="text-brand-stone/85 hover:text-brand-champagne">Privacy Policy</Link></li>
                        <li><Link to="/terms-and-conditions" data-testid="footer-terms" className="text-brand-stone/85 hover:text-brand-champagne">Terms & Conditions</Link></li>
                        <li><Link to="/cookie-policy" data-testid="footer-cookies" className="text-brand-stone/85 hover:text-brand-champagne">Cookie Policy</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-6 text-xs text-brand-stone/65 leading-relaxed" data-testid="footer-legal-block">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                        <div className="space-y-1">
                            <div className="font-medium text-brand-stone/85">Optimum Hospitality Solutions Ltd</div>
                            <div>Suite RA01, 195–197 Wood Street, London E17 3NU, United Kingdom</div>
                            <div data-testid="footer-company-number">
                                Company Registration Number: <span className="font-medium text-brand-stone/85">{COMPANY.registrationNumber}</span>
                            </div>
                        </div>
                        <div className="text-brand-stone/55 md:text-right">
                            © {new Date().getFullYear()} Optimum Hospitality Solutions Ltd. All Rights Reserved.<br/>
                            Hospitality consulting for owners, operators and investors across the UK.
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
